/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {forwardRef, Inject, Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {CandidateBackup, EmployeeBackup} from '../../+ess/employee-self.service';

import {CliEmpAttributes} from '../../models/models.interfaces';
import {ApiService, Data} from '../api/';
import {RolesService} from '../authentication/roles/roles.service';
import {QueryParamBuilder} from '../utils/query-param-builder';
import {Team} from './teams.model';
import {ProfileMode} from '../../app.constants';

/**
 * Employees service that expose access to employee information.
 */
@Injectable()
export class TeamsService {

  constructor(@Inject(forwardRef(() => RolesService)) private rolesService: RolesService,
              private api: ApiService) {
  }

  public getWorkCycles(params: { ids: any[]; periodBegin: string; periodEnd: string }) {
    return this.api.post(`/ga/api/v1/empContract/workcycles`, params);
  }

  public getAbsences(params: { ids: any[]; periodBegin: string; periodEnd: string, profileMode: ProfileMode }) {
    const { profileMode, ...filteredParams } = params;
    return this.api.post(`/ga/api/v1/empContract/absences?profile-mode=${profileMode}`, filteredParams);
  }

  public getPendingAbsences(params: { ids: any[]; periodBegin: string; periodEnd: string }) {
    return this.api.post(`/ga/api/v1/empAbsence/getPendingAbsences`, params);
  }

  public getTeamsList(): Observable<any> {
    return this.api.get(`/ga/api/v1/teams`);
  }

  public addContractToTeam(teamId: number, contractId: number): Observable<any> {
    return this.api.post(`/ga/api/v1/teams/${teamId}/members/${contractId}`);
  }

  public removeContractFromTeam(teamId: number, contractId: number): Observable<Team> {
    return this.api.delete(`/ga/api/v1/teams/${teamId}/members/${contractId}`);
  }

  public createTeam(data: any): Observable<any> {
    return this.api.post(`/ga/api/v1/teams`, data);
  }

  public deleteTeam(teamId: number): Observable<any> {
    return this.api.delete(`/ga/api/v1/teams/${teamId}`);
  }

  public getDefaultSubordinates(searchTerm = ''): Observable<Array<Data<CliEmpAttributes>>> {
    return this.api.get(`/ga/api/v1/teams/default`, {
      requestOptions: {
        params: {
          search: searchTerm
        }
      }
    });
  }

  public getIndirectSubordinates(searchTerm = ''): Observable<Array<Data<CliEmpAttributes>>> {
    return this.api.ga.get(`/teams/indirect`, {
      requestOptions: {
        params: {
          search: searchTerm
        }
      }
    });
  }

  public getAllSubordinates(searchTerm = ''): Observable<Array<Data<CliEmpAttributes>>> {
    return this.api.ga.get(`/teams/all`, {
      requestOptions: {
        params: {
          search: searchTerm
        }
      }
    });
  }

  public getBackupSubordinates(searchTerm = ''): Observable<Array<Data<CliEmpAttributes>>> {
    return this.api.ga.get(`/teams/backup`, {
      requestOptions: {
        params: {
          search: searchTerm
        }
      }
    });
  }

  public getFutureSubordinates(searchTerm = ''): Observable<Array<Data<CliEmpAttributes>>> {
    return this.api.ga.get(`/teams/future`, {
      requestOptions: {
        params: {
          search: searchTerm
        }
      }
    });
  }

  public getTeamMembers(teamId: number, seeInactiveContracts = false, seeInactiveEmployees = true) {
    return this.api.get(`/ga/api/v1/teams/${teamId}/members`, {
      requestOptions: {
        params: {
          seeInactiveContracts: seeInactiveContracts,
          seeInactiveEmployees: seeInactiveEmployees
        }
      }
    });
  }

  public getAllEmployees(contractBeginDate: string, contractEndDate: string, search: string) {
    return this.api.get(
      new QueryParamBuilder('/ga/api/v1/mss/employee')
        .addIfNotNull('search', search)
        .addIfNotNull('contract-begin-date', contractBeginDate)
        .addIfNotNull('contract-end-date', contractEndDate)
        .build())
      .pipe(
        map(employees => employees.map(employee => {
          employee.name = `${employee.firstName} ${employee.lastName}`;
          employee.photoLink = {
            rel: 'photo',
            href: `/ga/api/v1/cliEmp/${employee.id}/picture/${employee.photoUuid}`,
            type: 'GET'
          };
          return employee;
        }))
      );
  }

  public addBackup(backupForm: CandidateBackup) {
    return this.api.post('/ga/api/v1/mss/backup', backupForm);
  }

  public getBackups(): Observable<EmployeeBackup[]> {
    return this.api.ga.get(`/mss/backup/active`).pipe(
      map(response => response.map(employee => ({
        ...employee,
        backupEmployee: {
          ...employee.backupEmployee,
          photoUuid: employee.backupEmployee?.photoUuid ?
            `/ga/api/v1/cliEmp/${employee.backupEmployee.id}/picture/${employee.backupEmployee.photoUuid}` :
            null
        }
      })))
    );
  }

  public deleteBackup(backupId: number) {
    return this.api.ga.delete(`/mss/backup/${backupId}`);
  }
}
